/* «Свой аватар» на движке шаблона: состояние экрана S (features/avatar/engine.ts) ↔ входы формы сервера (план движений §2.2 push/pull).
   Вид (AvatarView) — то, что мастер говорит заказу: имя варианта, стиль, голос, согласие и фото «Вы».
   - viewFromEngine(h, o) — вид из S (зеркало S → входы в syncFoot);
   - inputsOf(form, view, prev) — входы по виду; пишет только опубликованные ключи (родные правила AvatarOrderForm): variant_name, base,
     голос (voice — объект {mode, voice_id} JSON-текстом, или прежний voice_id числом: avatar-voice.ts), consent_accepted, self_photos
     (фото «Вы» в заказ — только с согласием, Q-M8), consent_version (одно значение формы); id снимков (photo_01…photo_20) пишет мост
     загрузок (лоток слотов) — здесь их не трогают;
   - viewOf(form, inputs) — обратно: только то, что входы определяют (повтор из «Истории», «Изменить» после перезагрузки, заявка родной формы);
   - applyView(h, view) — вид в S (имя, стиль, голос, согласие); снимки — у сервера экрана.
   Пределы — avatarLimits: constraints формы, затем документ экрана (GET /app/studio/screens/avatar), затем значения шаблона.
   Чистые inputsOf и viewOf проходят круг: viewOf(form, inputsOf(form, view, {})) ⊆ view (avatar-inputs.test.ts). */
import { studioExpectedPrice, type StudioForm } from '../studio/studio-api';
import { fieldOf, firstField, photoSlots, type FormField } from '../shoot/kit/form-model';

type Inputs = Record<string, string>;
/** Голос варианта: «Позже», свой клон, голос каталога (значения S.vm шаблона) */
export type AvatarVoiceMode = 'later' | 'clone' | 'cat';
export interface AvatarView {
  /** Имя варианта без пробелов по краям ('' — нет) */
  name: string;
  /** Стиль: real · illus · 3d */
  base: string;
  vm: AvatarVoiceMode;
  /** Голос (id каталога) у clone и cat */
  voice: number | null;
  /** Отметка «На фото я или у меня есть согласие» */
  ok: boolean;
  /** Фото «Вы» идут в заказ (self_photos) */
  self: boolean;
}
export interface AvatarLimits {
  /** Цена варианта, C (0 — работа бесплатна) */
  price: number;
  min: number;
  good: number;
  great: number;
  max: number;
  nameMax: number;
  /** «Обычно около N минут» — только если документ экрана его публикует */
  minutes: number | null;
}

const VOICE_KEYS = ['voice_id', 'voice'] as const;
const num = (value: unknown): number | null => {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
};
const allowed = (field: FormField | undefined, value: string) => !!field && (!field.values.length || field.values.includes(value));
/** Поле голоса — объект {mode, voice_id} (контракт экрана) или прежнее число voice_id */
const objectVoice = (field: FormField) => field.type === 'object' || field.key === 'voice';
const codepoints = (value: string) => Array.from(value).length;

/** Поле голоса формы (как родная форма: voice_id, затем voice) */
export const avatarVoiceField = (form: StudioForm | null | undefined) => firstField(form, VOICE_KEYS);
/** Ключи снимков формы по порядку (photo_01…photo_20) */
export const avatarSlots = (form: StudioForm | null | undefined) => photoSlots(form).map(field => field.key);

/** Пределы мастера: constraints формы → документ экрана → шаблон; цена — цена формы по входам (у бесплатной работы 0) */
export function avatarLimits(form: StudioForm | null | undefined, doc: Record<string, unknown> | null | undefined, inputs: Inputs = {}): AvatarLimits {
  const c = (form?.constraints ?? {}) as Record<string, unknown>;
  const d = doc ?? {};
  const slots = avatarSlots(form).length;
  const min = num(c.min_photos) ?? num(d.min_photos) ?? 5;
  const max = Math.max(min, num(c.max_photos) ?? num(d.max_photos) ?? (slots || 20));
  const good = Math.min(max, Math.max(min, num(d.good_photos) ?? 10));
  const great = Math.min(max, Math.max(good, num(d.great_photos) ?? 15));
  const field = fieldOf(form, 'variant_name');
  const nameMax = Math.min(num(c.name_max_unicode_codepoints) ?? num(d.name_max) ?? 24, num(field?.max_length) ?? 24);
  const price = form && form.chargeable !== false ? studioExpectedPrice(form, inputs) : 0;
  return { price: Number.isSafeInteger(price) && price > 0 ? price : 0, min, good, great, max, nameMax, minutes: num(d.minutes) };
}

/** Значение поля голоса по виду; «Позже» и голос без id — нет значения (родная форма снимает поле) */
export function voiceValue(field: FormField, vm: AvatarVoiceMode, voice: number | null): string | undefined {
  if (vm === 'later' || voice === null) return undefined;
  return objectVoice(field) ? JSON.stringify({ mode: vm === 'cat' ? 'catalog' : 'clone', voice_id: voice }) : String(voice);
}
/** Голос из значения поля: объект {mode, voice_id} (JSON-текстом во входах формы, объектом в теле заявки) или число (прежнее поле — свой клон) */
export function voiceOfValue(field: FormField, raw: unknown): { vm: AvatarVoiceMode; voice: number | null } {
  if (!raw) return { vm: 'later', voice: null };
  if (!objectVoice(field)) {
    const id = num(typeof raw === 'number' ? raw : String(raw));
    return id ? { vm: 'clone', voice: id } : { vm: 'later', voice: null };
  }
  try {
    const value = (typeof raw === 'string' ? JSON.parse(raw) : raw) as { mode?: unknown; voice_id?: unknown };
    const id = num(value?.voice_id);
    if (!id || (value.mode !== 'clone' && value.mode !== 'catalog')) return { vm: 'later', voice: null };
    return { vm: value.mode === 'catalog' ? 'cat' : 'clone', voice: id };
  } catch {
    return { vm: 'later', voice: null };
  }
}

/** Входы по виду: только опубликованные ключи; id снимков и прочие ключи prev остаются */
export function inputsOf(form: StudioForm | null | undefined, view: AvatarView, prev: Inputs): Inputs {
  const next = { ...prev };
  if (!form) return next;
  const set = (key: string, value: string | undefined) => {
    if (!fieldOf(form, key)) return;
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
  };
  const limits = avatarLimits(form, null);
  set('variant_name', view.name ? Array.from(view.name).slice(0, limits.nameMax).join('') : undefined);
  const base = fieldOf(form, 'base');
  set('base', base && allowed(base, view.base) ? view.base : undefined);
  const voice = avatarVoiceField(form);
  if (voice) set(voice.key, voiceValue(voice, view.vm, view.voice));
  const consent = fieldOf(form, 'consent_accepted');
  set('consent_accepted', consent && view.ok && allowed(consent, 'true') ? 'true' : undefined);
  set('self_photos', view.self ? 'true' : undefined);
  const version = fieldOf(form, 'consent_version');
  if (version?.values.length === 1 && !next.consent_version) next.consent_version = version.values[0];
  return next;
}

/** Вид из входов: только то, что они определяют (входы формы — строки; тело заявки несёт JSON-поля значениями: voice — объект, self_photos — true) */
export function viewOf(form: StudioForm | null | undefined, inputs: Record<string, unknown>): Partial<AvatarView> {
  if (!form) return {};
  const out: Partial<AvatarView> = {};
  const text = (key: string) => (typeof inputs[key] === 'string' ? (inputs[key] as string) : '');
  if (fieldOf(form, 'variant_name')) out.name = text('variant_name').trim();
  const base = fieldOf(form, 'base');
  if (base && text('base') && allowed(base, text('base'))) out.base = text('base');
  const voice = avatarVoiceField(form);
  if (voice) Object.assign(out, voiceOfValue(voice, inputs[voice.key]));
  const yes = (key: string) => inputs[key] === 'true' || inputs[key] === true;
  if (fieldOf(form, 'consent_accepted')) out.ok = yes('consent_accepted');
  if (fieldOf(form, 'self_photos')) out.self = yes('self_photos');
  return out;
}

/** Чего не хватает заказу по входам сверх правил шаблона (lack): длинное имя, стиль; '' — всё есть */
export function avatarMissing(form: StudioForm | null | undefined, inputs: Inputs): string {
  if (!form) return '';
  const limits = avatarLimits(form, null);
  const name = (inputs.variant_name ?? '').trim();
  if (name && codepoints(name) > limits.nameMax) return 'Сократите имя варианта';
  const base = fieldOf(form, 'base');
  if (base?.required && !inputs.base) return 'Выберите стиль';
  return '';
}

/** То, что вид читает у движка */
/** То, что вид читает и пишет у S движка */
export interface AvatarViewState {
  name: string;
  base: string;
  vm: string;
  voice: unknown;
  ok: string | null;
  vgone: string;
  photos: ReadonlyArray<{ me?: boolean; st: string }>;
}
export interface AvatarViewHooks {
  S: AvatarViewState;
}
/** Вид из S; o.rights — права на материалы даны (фото «Вы» идут в заказ только с ними, Q-M8) */
export function viewFromEngine(h: AvatarViewHooks, o: { rights: boolean }): AvatarView {
  const S = h.S;
  const vm: AvatarVoiceMode = S.vm === 'clone' || S.vm === 'cat' ? S.vm : 'later';
  return {
    name: S.name.trim(),
    base: S.base,
    vm,
    voice: vm === 'later' ? null : num(S.voice),
    ok: !!S.ok,
    self: o.rights && S.photos.some(p => p.me && p.st === 'ok'),
  };
}
/** Вид в S (имя, стиль, голос, согласие); снимки и фото «Вы» — у сервера экрана */
export function applyView(h: AvatarViewHooks, view: Partial<AvatarView>): void {
  const S = h.S;
  if (view.name !== undefined) S.name = view.name;
  if (view.base !== undefined) S.base = view.base;
  if (view.vm !== undefined) {
    S.vm = view.vm;
    S.voice = view.vm === 'later' ? null : view.voice;
    S.vgone = '';
  }
  if (view.ok !== undefined) S.ok = view.ok ? 'me' : null;
}
